import React, { useState, useEffect, useCallback } from 'react';
import Quiz from './components/Quiz';
import StartScreen from './components/StartScreen';
import ResultsScreen from './components/ResultsScreen';
import ReviewScreen from './components/ReviewScreen';
import HistoryScreen from './components/HistoryScreen';
import FlashcardScreen from './components/FlashcardScreen';
import LoginScreen from './components/LoginScreen';
import AdminScreen from './components/AdminScreen';
import PrivacyPolicyScreen from './components/PrivacyPolicyScreen';
import { Question, QuizResult, UserAnswer, HistoryEntry, QuizMode, CustomQuizConfig, FlashcardConfig, User, SiteConfig } from './types';
import { calculateScore } from './utils/score';
import apiClient, { getCurrentUser, logoutUser, setApiClientSimCode } from './auth/service';
import SimuladoSelectionScreen from './components/SimuladoSelectionScreen';
import { Simulado as ISimuladoType } from './types';
import ChangePasswordModal from './components/ChangePasswordModal';

const App: React.FC = () => {
    const [quizState, setQuizState] = useState<'start' | 'active' | 'finished' | 'review' | 'history' | 'flashcards' | 'admin' | 'select-simulado'>('start');
    const [results, setResults] = useState<QuizResult | null>(null);
    const [resumeQuiz, setResumeQuiz] = useState(false);
    const [hasSavedProgress, setHasSavedProgress] = useState(false);
    const [quizMode, setQuizMode] = useState<QuizMode>('practice');
    const [quizHistory, setQuizHistory] = useState<HistoryEntry[]>([]);
    const [customQuizConfig, setCustomQuizConfig] = useState<CustomQuizConfig | undefined>(undefined);
    const [flashcardConfig, setFlashcardConfig] = useState<FlashcardConfig | undefined>(undefined);
    const [allQuestions, setAllQuestions] = useState<Question[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(null);
    const [theme, setTheme] = useState<'light' | 'dark'>('light');
    const [viewingPrivacyPolicy, setViewingPrivacyPolicy] = useState(false);
    const [siteConfig, setSiteConfig] = useState<SiteConfig>({
        pageTitle: '',
        mainTitle: '',
        siteUrl: '',
        siteLogoUrl: '', // Adicionado
        subtitle: '',
        logoUrl: '', // Logo do simulado
        activeSimName: '', // Nome do simulado
        bannerTrial: '' // Banner do trial
    });
    const [activeSim, setActiveSim] = useState<ISimuladoType | null>(null);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [trialInfo, setTrialInfo] = useState<{ usedSeconds: number; remainingSeconds: number; limitSeconds: number } | null>(null);
    const [trialDisplay, setTrialDisplay] = useState<string>('');
    const [displayLanguage, setDisplayLanguage] = useState<'pt' | 'en' | 'es'>('pt');

    // Set page title and favicon
    useEffect(() => { 
        document.title = siteConfig.pageTitle?.trim() ? siteConfig.pageTitle : 'Simulado'; 
        
        // Update favicon
        if (siteConfig.siteLogoUrl) {
            let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = siteConfig.siteLogoUrl;
        }
    }, [siteConfig.pageTitle, siteConfig.siteLogoUrl]);

    // Theme management
    useEffect(() => {
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        // no persistent storage per request
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
    };

    const fetchSiteConfig = useCallback(async () => {
        try {
            const config = await apiClient.get('/settings');
            setSiteConfig(config);
            
            // Se já tivermos um simulado selecionado, garantimos que os dados dele sejam usados
            const savedSimCode = sessionStorage.getItem('selectedSimCode');
            if (savedSimCode) {
                setApiClientSimCode(savedSimCode);
                try {
                    // Busca os dados do simulado específico
                    const sim = await apiClient.get('/simulados/active'); 
                    if (sim) {
                        setActiveSim(sim);
                    }
                } catch {}
            }
        } catch (err) {
            console.error("Failed to load site config, using defaults.", err);
        }
    }, []);

    const loadDataForUser = useCallback(async () => {
        if (!currentUser) return;
        setIsLoading(true);
        setError(null);
        try {
            try {
                const usage = await apiClient.get('/user/trial/usage');
                setTrialInfo(usage);
            } catch {}
                const [progressData, historyData, questionsData] = await Promise.all([
                apiClient.get('/progress'),
                apiClient.get('/history'),
                apiClient.get('/questions?all=1')
            ]);

            setHasSavedProgress(!!progressData.progress);

            const historyWithMaps: HistoryEntry[] = historyData.map((entry: any) => ({
                ...entry,
                answers: new Map<number, UserAnswer>(Object.entries(entry.answers).map(([k, v]) => [Number(k), v as UserAnswer])),
                timePerQuestion: new Map<number, number>(Object.entries(entry.timePerQuestion || {}).map(([k, v]) => [Number(k), v as number])),
            }));
            setQuizHistory(historyWithMaps);
            setAllQuestions(questionsData);

        } catch (err: any) {
            setError(`Failed to load data: ${err.message}`);
            console.error("Failed to load data:", err);
            if (err.message.includes('401')) { 
                handleLogout();
            }
        } finally {
            setIsLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser?.isTrial) {
            setTrialDisplay('');
            return;
        }

        // Se ainda não temos a informação do servidor, não inicializamos o contador com 3600
        if (!trialInfo) {
            setTrialDisplay('...');
            return;
        }

        let remainingLocal = trialInfo.remainingSeconds;

        const renderDisplay = () => {
            const mins = Math.floor(remainingLocal / 60);
            const secs = remainingLocal % 60;
            setTrialDisplay(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
        };

        renderDisplay();

        const tick = setInterval(() => {
            if (remainingLocal > 0) {
                remainingLocal -= 1;
                renderDisplay();
            } else if (remainingLocal <= 0) {
                clearInterval(tick);
                handleLogout('Seu período de teste acabou! Adquira uma assinatura para continuar.'); // Força logout quando o contador local chega a zero
            }
        }, 1000);

        const sync = setInterval(async () => {
            try {
                const usage = await apiClient.get('/user/trial/usage');
                setTrialInfo(usage);
                
                // Se expirou no servidor, forçar logout imediatamente
                if (usage.remainingSeconds <= 0) {
                    handleLogout('Seu período de teste acabou! Adquira uma assinatura para continuar.');
                    return;
                }

                // Sincroniza o contador local com o servidor se houver drift
                if (Math.abs(remainingLocal - usage.remainingSeconds) > 5) {
                    remainingLocal = usage.remainingSeconds;
                    renderDisplay();
                }
            } catch (err: any) {
                if (err.message.includes('403')) {
                    handleLogout();
                }
            }
        }, 10000); // Sincroniza a cada 10s para maior precisão

        return () => {
            clearInterval(tick);
            clearInterval(sync);
        };
    }, [currentUser, trialInfo === null]); // Só reinicia se o usuário mudar ou se trialInfo for resetado (login)

    // Check for logged-in user session on initial load
    // And fetch global data like questions and site config
    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                await fetchSiteConfig();
            } catch {}
            const user = await getCurrentUser();
            setCurrentUser(user);
            
            if (user) {
                // Busca info de trial imediatamente no init
                if (user.isTrial) {
                    apiClient.get('/user/trial/usage').then(setTrialInfo).catch(() => {});
                }

                const savedSimCode = sessionStorage.getItem('selectedSimCode');
                if (savedSimCode) {
                    setApiClientSimCode(savedSimCode);
                    try {
                        const sim = await apiClient.get('/simulados/active');
                        if (sim) {
                            setActiveSim(sim);
                        }
                    } catch {}
                } else {
                    // Se não tiver simulado selecionado, mas estiver logado, vai para a seleção
                    setQuizState('select-simulado');
                }

                try {
                    const questionsData = await apiClient.get('/questions?all=1');
                    setAllQuestions(questionsData);
                } catch (err: any) {
                    setError(`Failed to load initial data: ${err.message}`);
                }
            }
            setIsLoading(false);
        };
        init();
    }, []);

    // Load user-specific data when user logs in or app loads with a user
    useEffect(() => {
        if (currentUser) {
            loadDataForUser();
        } else {
            setHasSavedProgress(false);
            setQuizHistory([]);
            setAllQuestions([]);
        }
    }, [currentUser, loadDataForUser]);

    const handleLoginSuccess = async (user: User) => {
        setCurrentUser(user);
        
        // Busca info de trial imediatamente após login para evitar delay no banner
        if (user.isTrial) {
            try {
                const usage = await apiClient.get('/user/trial/usage');
                setTrialInfo(usage);
            } catch (err) {
                console.error("Erro ao buscar trial inicial:", err);
            }
        }

        // Se for admin ou tiver mais de um simulado, deixa escolher
        if (user.role === 'admin' || (user.enrolledSimulados && user.enrolledSimulados.length > 1)) {
            setQuizState('select-simulado');
        } else if (user.enrolledSimulados && user.enrolledSimulados.length === 1) {
            // Se tiver apenas um, seleciona automaticamente
            const simCode = user.enrolledSimulados[0];
            setApiClientSimCode(simCode);
            setQuizState('start');
            await fetchSiteConfig();
        } else {
            // Nenhum simulado ou erro, mostra a tela de seleção para dar feedback
            setQuizState('select-simulado');
        }
    };

    const handleSimuladoSelect = async (sim: ISimuladoType) => {
        setApiClientSimCode(sim.code);
        setActiveSim(sim);
        setQuizState('start');
        await fetchSiteConfig();
        await loadDataForUser();
    };
    const handleLogout = async (message?: string) => {
        await logoutUser();
        setCurrentUser(null);
        setTrialInfo(null); // Resetar trial info no logout
        if (message) {
            setLoginErrorMessage(message);
        } else {
            setLoginErrorMessage(null);
        }
        setQuizState('start');
    };

    const startQuiz = async (options: { resume: boolean; mode?: QuizMode, customConfig?: CustomQuizConfig }) => {
        if (!currentUser) return;       
        if (!activeSim) {
            try {
                const sim = await apiClient.get('/simulados/active');
                if (sim) {
                    setActiveSim(sim);
                }
            } catch {}
        }
        if (options.resume) {
            try {
                const data = await apiClient.get('/progress');
                if (data?.progress) {
                    const progressMode: QuizMode = data.progress.mode;
                    setQuizMode(progressMode);
                    setCustomQuizConfig(data.progress.customConfig || undefined);
                }
            } catch (err) {
                console.error("Failed to load saved progress for resume", err);
            }
        } else {
            try {
                await apiClient.delete('/progress');
                setHasSavedProgress(false);
            } catch (err) {
                console.error("Failed to delete progress", err);
            }
            if (options.mode) setQuizMode(options.mode);
            setCustomQuizConfig(options.customConfig ? options.customConfig : undefined);
        }
        setResumeQuiz(options.resume);
        setQuizState('active');
        setResults(null);
    };
    
    const startFlashcards = (config: FlashcardConfig) => {
        setFlashcardConfig(config);
        setQuizState('flashcards');
    };

    const exitFlashcards = () => {
        setQuizState('start');
        setFlashcardConfig(undefined);
    };

    const finishQuiz = async (finalResults: QuizResult) => {
        if (!currentUser) return;

        // Start changing state immediately to show feedback
        setIsLoading(true);

        try {
            // Run progress deletion and history save in parallel
            const resultToUse: QuizResult = (typeof finalResults.passingScore === 'number' && finalResults.passingScore > 0)
                ? finalResults
                : (activeSim?.passingScore ? { ...finalResults, passingScore: activeSim.passingScore } : finalResults);

            const newHistoryEntry: HistoryEntry = {
                ...resultToUse,
                date: new Date().toISOString(),
                score: calculateScore(resultToUse)
            };

            const serializableEntry: any = {
                ...newHistoryEntry,
                answers: Object.fromEntries(newHistoryEntry.answers),
                timePerQuestion: Object.fromEntries(newHistoryEntry.timePerQuestion),
                questions: resultToUse.questions.map(q => ({ id: q.id, domain: q.domain })),
            };

            await Promise.all([
                apiClient.delete('/progress'),
                apiClient.post('/history', { historyEntry: serializableEntry })
            ]);

            setHasSavedProgress(false);
            setQuizHistory(prev => [newHistoryEntry, ...prev]);
            setResults(resultToUse);
            setQuizState('finished');

        } catch (err) {
            console.error("Failed to finish quiz:", err);
            setError("Could not save your results. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const restartQuiz = async () => {
        if (!currentUser) return;
        try {
            await apiClient.delete('/progress');
            setHasSavedProgress(false);
        } catch(err) {
            console.error("Failed to delete progress on restart", err);
        }
        setQuizState('start');
        setResults(null);
    }

    const handleReview = () => setQuizState('review');
    const handleExitReview = () => setQuizState('finished');
    const showHistory = () => setQuizState('history');
    const showAdminPanel = () => setQuizState('admin');
    const exitHistory = () => setQuizState('start');

    const clearHistory = async () => {
        if (!currentUser) return;
        try {
            await apiClient.delete('/history');
            setQuizHistory([]);
        } catch (err) {
            console.error("Failed to clear history", err);
            setError("Could not clear history. Please try again.");
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return <div className="text-center text-xl p-8 text-gray-700 dark:text-slate-200">Carregando...</div>;
        }
        if (!currentUser) {
            return <LoginScreen onLoginSuccess={handleLoginSuccess} onShowPrivacyPolicy={() => setViewingPrivacyPolicy(true)} loginError={loginErrorMessage} />;
        }
        if (error) {
            return <div className="text-center text-xl p-8 text-red-600 dark:text-red-400">{error}</div>;
        }

        switch (quizState) {
            case 'start':
                return <StartScreen onStart={startQuiz} onStartFlashcards={startFlashcards} hasSavedProgress={hasSavedProgress} onShowHistory={showHistory} hasHistory={quizHistory.length > 0} allQuestions={allQuestions} currentUser={currentUser} onShowAdminPanel={showAdminPanel} subtitle={displaySubtitle} displayLanguage={displayLanguage} />;
            case 'select-simulado':
                return currentUser && <SimuladoSelectionScreen user={currentUser} onSelect={handleSimuladoSelect} onLogout={handleLogout} displayLanguage={displayLanguage} />;
            case 'active':
                return <Quiz onFinish={finishQuiz} resume={resumeQuiz} mode={quizMode} customConfig={customQuizConfig} allQuestions={allQuestions} currentUser={currentUser} examDurationSeconds={activeSim?.examDurationSeconds} questionCountOverride={activeSim?.questionCount} passingScore={activeSim?.passingScore} displayLanguage={displayLanguage} />;
            case 'finished':
                return results && <ResultsScreen results={results} onRestart={restartQuiz} onReview={handleReview} passingScore={activeSim?.passingScore} displayLanguage={displayLanguage} />;
            case 'review':
                return results && <ReviewScreen results={results} onExit={handleExitReview} displayLanguage={displayLanguage} />;
            case 'history':
                return <HistoryScreen history={quizHistory} onExit={exitHistory} onClear={clearHistory} />;
            case 'flashcards':
                return flashcardConfig && <FlashcardScreen config={flashcardConfig} history={quizHistory} onExit={exitFlashcards} allQuestions={allQuestions} displayLanguage={displayLanguage} />;
            case 'admin':
                return currentUser?.role === 'admin' && <AdminScreen onExit={() => setQuizState('start')} currentConfig={siteConfig} onConfigChange={(cfg) => setSiteConfig(cfg)} />;
            default:
                return <LoginScreen onLoginSuccess={handleLoginSuccess} onShowPrivacyPolicy={() => setViewingPrivacyPolicy(true)} />;
        }
    }

    const isQuizRunning = quizState === 'active' || quizState === 'review' || quizState === 'finished' || (quizState === 'start' && activeSim);
    const displayLogo = (isQuizRunning && activeSim?.logoUrl) ? activeSim.logoUrl : siteConfig.siteLogoUrl;
    const displayTitle = (isQuizRunning && activeSim?.name) ? activeSim.name : siteConfig.mainTitle;
    const displaySubtitle = (isQuizRunning && activeSim?.subtitle) ? activeSim.subtitle : siteConfig.subtitle;

    const handleHomeClick = () => {
        if (currentUser) {
            setQuizState('select-simulado');
            setActiveSim(null);
            // Limpa o simCode da sessão para forçar nova seleção se quiser
            sessionStorage.removeItem('selectedSimCode');
            setApiClientSimCode('default');
        } else {
            setQuizState('start');
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200 flex flex-col items-center p-4 sm:p-6 lg:p-8">            
            {viewingPrivacyPolicy && <PrivacyPolicyScreen onClose={() => setViewingPrivacyPolicy(false)} />}
            
            {/* Banner do Trial */}
            {currentUser?.isTrial && (siteConfig.bannerTrial || trialDisplay) && (
                <div className="w-full bg-blue-600 text-white py-2 px-4 text-center text-sm font-medium animate-fade-in mb-4 rounded-lg shadow-sm flex items-center justify-center gap-2">
                    {trialDisplay && (
                        <span className="bg-white/20 px-2 py-0.5 rounded font-mono font-bold flex items-center gap-1 border border-white/30">
                            <i className="fas fa-clock"></i>
                            {trialDisplay}
                        </span>
                    )}
                    {siteConfig.bannerTrial && (
                        <span>{siteConfig.bannerTrial}</span>
                    )}
                </div>
            )}

            <header className="w-full max-w-7xl mx-auto text-center mb-8 relative flex justify-center items-center">
                <div className="absolute top-0 left-0 flex items-center gap-2">
                    {currentUser && (
                        <button
                            onClick={handleHomeClick}
                            className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                            aria-label="Home"
                            title="Home"
                        >
                            <i className="fas fa-home"></i>
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {siteConfig.siteUrl && !isQuizRunning ? (
                        <a 
                            href={siteConfig.siteUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                        >
                            {displayLogo && displayLogo.trim().length > 0 && (
                                <img
                                    src={displayLogo}
                                    alt="Logo"
                                    className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                />
                            )}
                            <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">
                                {displayTitle}
                            </h1>
                        </a>
                    ) : (
                        <div className="flex items-center gap-2">
                            {displayLogo && displayLogo.trim().length > 0 && (
                                <img
                                    src={displayLogo}
                                    alt="Logo"
                                    className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                />
                            )}
                            <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">
                                {displayTitle}
                            </h1>
                        </div>
                    )}
                </div>
                <div className="absolute top-0 right-0 flex items-center gap-2 sm:gap-4">
                    {currentUser && (
                         <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold hidden sm:inline">
                                {displayLanguage === 'en'
                                    ? `Hello, ${currentUser.firstName}!`
                                    : displayLanguage === 'es'
                                        ? `Hola, ${currentUser.firstName}!`
                                        : `Olá, ${currentUser.firstName}!`}
                            </span>
                            <button
                                onClick={() => setShowChangePassword(true)}
                                className="p-2 rounded-full bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                                aria-label="Alterar Minha Senha"
                                title="Alterar Minha Senha"
                            >
                                <i className="fas fa-key"></i>
                            </button>
                             <button
                                onClick={() => handleLogout()}
                                className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                                aria-label="Sair"
                                title="Sair"
                            >
                                <i className="fas fa-sign-out-alt"></i>
                            </button>
                        </div>
                    )}
                    {currentUser?.role === 'admin' && (
                        <button
                            onClick={showAdminPanel}
                            className="p-2 rounded-full bg-indigo-500 text-white hover:bg-indigo-600 transition-colors"
                            aria-label="Painel de Administração"
                            title="Painel de Administração"
                        >
                            <i className="fas fa-user-shield"></i>
                        </button>
                    )}
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                        aria-label="Toggle theme"
                    >
                        {theme === 'light' ? (
                            <svg key="moon-svg" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>
                        ) : (
                            <svg key="sun-svg" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.707.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" /></svg>
                        )}
                    </button>
                    {currentUser && (
                        <select
                            value={displayLanguage}
                            onChange={(e) => setDisplayLanguage(e.target.value as 'pt' | 'en' | 'es')}
                            className="p-2 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm"
                            aria-label="Idioma de exibição"
                            title="Idioma de exibição"
                        >
                            <option value="pt">BR</option>
                            <option value="en">EN</option>
                            <option value="es">ES</option>
                        </select>
                    )}
                </div>
            </header>
            <main className="w-full max-w-7xl mx-auto flex-grow flex justify-center items-center">
                {renderContent()}
            </main>
            {currentUser && (
                <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} currentUser={currentUser} />
            )}
        </div>
    );
};

export default App;
